/* Карточка проекта аккаунтинга. ТЗ 6.6, 5.11, 5.13, 7.7. */

import { ACC_STAGES, accStageColor, canEdit, crmLevel, ddmm, humanDate, moneyEdit, rub, stageTint } from "@mera/shared";
import React from "react";
import { useNavigate, useParams } from "../lib/router.jsx";

import Feed from "../components/Feed.jsx";
import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelEntityCard } from "../components/skeleton.jsx";
import {
  ErrorState, Eyebrow, Hint, Metric, MetricGrid, Money, Sep, StageChips, TierBadge,
} from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";

export default function ProjectCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me, meta, today, reloadAlerts } = useApp();
  const { data: p, loading, error, refetch } = useFetch(`/projects/${id}`);

  if (loading) return <Screen back><SkelEntityCard /></Screen>;
  if (error) return <Screen back><ErrorState text={error} onRetry={refetch} /></Screen>;

  const stages = p.accStages || ACC_STAGES.dialogic;
  const isDialogicAcc = p.metrics.kind === "dialogic-accounting";
  // Правки проекта — по воронке аккаунтинга его бизнеса; операции — по правам на деньги
  const canWrite = canEdit(crmLevel(me, p.businessId, "accounting"));
  const opEdit = moneyEdit(me);

  const patch = async (body) => {
    await api.patch(`/projects/${id}`, body);
    haptic.success();
    refetch();
    reloadAlerts();
  };

  // План и факт правятся отдельной страницей — как все формы
  const openPlan = canWrite ? () => { haptic.light(); navigate(`/project/${id}/plan`); } : undefined;

  const toCrm = async () => {
    const res = await api.post(`/projects/${id}/move-to-crm`);
    haptic.success();
    navigate(`/deal/${res.dealId}`);
  };

  /* Факт считает сервер: журнал по привязке к проекту плюс внесённый руками
     (у старых проектов операций нет — деньги записаны цифрой на странице плана) */
  const planFact = {
    incomeFact: p.metrics.factRevenue || 0,
    expenseFact: p.metrics.factExpenses || 0,
  };

  return (
    <Screen back>
      {/* Шапка: тир в рост двух строк, в подстроке тип работ,
          справа дата добавления сделки и бизнес */}
      <div className="px-4 pt-1">
        <div className="card-tg px-4 py-3">
          <div className="flex items-center gap-3">
            {p.dealTier ? <TierBadge tier={p.dealTier} size={44} /> : null}
            <div className="min-w-0 flex-1">
              <div className="display text-[19px] font-semibold leading-tight truncate">{p.name}</div>
              <div className="text-[13px] truncate" style={{ color: "var(--tg-hint)" }}>
                {p.type || "Тип работ не указан"}
              </div>
            </div>
            <div className="shrink-0 text-right text-[12px]" style={{ color: "var(--tg-hint)" }}>
              {p.deal?.added ? <div className="num">{humanDate(p.deal.added)}</div> : null}
              <div className="font-medium truncate max-w-[120px]">
                {(meta?.businesses || []).find((b) => b.id === p.businessId)?.name || "—"}
              </div>
            </div>
          </div>

          {/* Правка полей проекта живёт здесь: своего раздела у проектов больше нет,
              они заводятся вместе со сделкой. Там же, в форме, и удаление. */}
          <div className="mt-2 flex gap-2">
            {me?.isAdmin ? (
              <button
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[14px] font-semibold active:opacity-70"
                style={{ background: "color-mix(in srgb, var(--tg-button) 12%, transparent)", color: "var(--tg-button)" }}
                onClick={() => { haptic.light(); navigate(`/edit/projects/${id}`); }}
              >
                <Ion.pencil size={14} />
                Изменить
              </button>
            ) : null}
            {p.dealId ? (
              <button
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[14px] font-semibold active:opacity-70"
                style={{ background: "color-mix(in srgb, var(--tg-button) 12%, transparent)", color: "var(--tg-button)" }}
                onClick={toCrm}
              >
                <Ion.swapHorizontal size={14} />
                В воронку продаж
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Стадия — чипами под шапкой, цветом своей колонки, как на карточке разработки */}
      {p.accStage ? (
        <StageChips
          stages={stages}
          value={p.accStage}
          onChange={(s) => { if (!canWrite) return; haptic.select(); patch({ accStage: s }); }}
          colorOf={(s) => {
            const business = (meta?.businesses || []).find((b) => b.id === p.businessId);
            return stageTint(business, "acc", s, accStageColor(s, stages));
          }}
        />
      ) : null}

      {/* Финблок (ТЗ 6.6, 7.7) */}
      <Eyebrow>{isDialogicAcc ? "Метрики аккаунтинга" : "Экономика проекта"}</Eyebrow>
      {isDialogicAcc ? (
        <MetricGrid>
          {/* В портфель MRR идёт только с подписочной стадии, но в карточке его видно
              сразу — иначе вписанная цифра пряталась бы за прочерком до перевода */}
          <Metric label="MRR" value={p.deal?.mrr ? rub(p.deal.mrr) : "—"} sub="₽/мес" onClick={openPlan} />
          <Metric
            label="Стоимость внедрения"
            value={rub(p.metrics.implementationPrice)}
            onClick={openPlan}
          />
          <Metric label="Факт выручка · весь период" value={rub(p.metrics.factRevenue)} color="var(--fin-green)" />
          <Metric
            label="Себестоимость · весь период"
            value={rub(p.metrics.costTotal)}
            color="var(--fin-red)"
            onClick={openPlan}
          />
        </MetricGrid>
      ) : (
        <MetricGrid cols={3}>
          <Metric label="Чек" value={rub(p.metrics.income)} onClick={openPlan} />
          <Metric label="Расходы" value={rub(p.metrics.expenses)} color="var(--fin-red)" onClick={openPlan} />
          <Metric
            label="Прибыль"
            value={rub(p.metrics.profit)}
            color={p.metrics.profit >= 0 ? "var(--fin-green)" : "var(--fin-red)"}
          />
        </MetricGrid>
      )}

      {/* Операции · план / факт (ТЗ 6.6). Для Dialogic-аккаунтинга блок скрыт */}
      {!isDialogicAcc ? (
        <>
          <div className="flex items-center justify-between px-4 pt-4 pb-1.5">
            <span className="eyebrow px-0 pt-0 pb-0">Операции · план / факт</span>
            {opEdit ? (
              <button
                className="inline-flex items-center gap-1 text-[13px] font-semibold active:opacity-60"
                style={{ color: "var(--tg-link)" }}
                onClick={() => { haptic.light(); navigate(`/edit/operations/new?proj=${id}`); }}
              >
                <Ion.add size={14} /> Операция
              </button>
            ) : null}
          </div>
          <div className="px-4">
            <div className="card-tg py-1">
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[14px]" style={{ color: "var(--tg-hint)" }}>Доходы</span>
                <div className="flex items-center gap-3">
                  <button className="num text-[14px] font-semibold active:opacity-60"
                    style={{ color: canWrite ? "var(--tg-link)" : "var(--tg-hint)" }}
                    onClick={openPlan}>
                    план {rub(p.income)}
                  </button>
                  <Money value={planFact.incomeFact} sign="in" className="text-[14px]" />
                </div>
              </div>
              <Sep />
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[14px]" style={{ color: "var(--tg-hint)" }}>Расходы</span>
                <div className="flex items-center gap-3">
                  <button className="num text-[14px] font-semibold active:opacity-60"
                    style={{ color: canWrite ? "var(--tg-link)" : "var(--tg-hint)" }}
                    onClick={openPlan}>
                    план {rub(p.expenses)}
                  </button>
                  <Money value={planFact.expenseFact} sign="out" className="text-[14px]" />
                </div>
              </div>
            </div>
          </div>

          {p.operations.length ? (
            <div className="px-4 pt-2">
              <div className="card-tg py-1">
                {p.operations.map((o, i) => (
                  <React.Fragment key={o.id}>
                    {i > 0 ? <Sep /> : null}
                    <div className="flex items-center justify-between gap-3 px-4 py-2">
                      <div className="min-w-0">
                        <div className="text-[13px] truncate">{o.cp} · {o.cat}</div>
                        <Hint>{ddmm(o.date)} · {o.acc}</Hint>
                      </div>
                      <Money value={o.amount} sign={o.type === "Поступление" ? "in" : "out"} className="text-[13px]" />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Повторяющийся платёж (ТЗ 5.11, 6.6) */}
      {p.dealId ? (
        <>
          <Eyebrow>Повторяющийся платёж</Eyebrow>
          <div className="px-4">
            <button
              className="w-full card-tg px-4 py-3 flex items-center gap-3 active:opacity-70 text-left"
              onClick={canWrite ? () => { haptic.light(); navigate(`/schedule/${id}`); } : undefined}
            >
              <Ion.repeat size={17} style={{ color: p.schedule?.active ? "var(--fin-blue)" : "var(--tg-hint)" }} />
              <div className="min-w-0 flex-1">
                {p.schedule?.active ? (
                  <>
                    <div className="text-[15px] font-medium">
                      {rub(p.schedule.amount)} · {p.schedule.dayOfMonth || p.schedule.day}-го числа
                    </div>
                    <Hint>{p.schedule.kind} · алерты за 3 дня и в день оплаты</Hint>
                  </>
                ) : (
                  <>
                    <div className="text-[15px] font-medium">Не настроен</div>
                    <Hint>Включите, чтобы получать напоминания о счёте и оплате</Hint>
                  </>
                )}
              </div>
              <Ion.chevronForward size={16} style={{ color: "var(--tg-hint)" }} />
            </button>
          </div>
        </>
      ) : null}

      {/* Материалы */}
      {p.materials?.length ? (
        <>
          <Eyebrow>Материалы</Eyebrow>
          <div className="hscroll flex gap-2 px-4">
            {p.materials.map((m, i) => (
              <div key={i} className="shrink-0 card-tg px-3 py-2 flex items-center gap-2 max-w-[240px]">
                {m.kind === "link" ? (
                  <Ion.link size={13} style={{ color: "var(--tg-hint)" }} />
                ) : (
                  <Ion.attach size={13} style={{ color: "var(--tg-hint)" }} />
                )}
                <span className="text-[13px] truncate">{m.t}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* Фид объединяет записи проекта и связанной сделки; разделы рисует сам */}
      <Feed scope="projects" id={id} items={p.feed} today={today} onChanged={refetch} canEdit={canWrite} />
    </Screen>
  );
}

